import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  LuPlus,
  LuSearch,
  LuEllipsis,
  LuChevronLeft,
  LuChevronRight,
} from "react-icons/lu";
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ThemedSelect from "components/FormControls/ThemedSelect";
import PickDate from "components/DatePicker/DatePicker";
import { format, parseISO } from "date-fns";
import ListingActionMenu from "components/ListingActionMenu/ListingActionMenu";
import { TableSkeleton } from "components/Skeleton";
import { ClearableSearchInput } from "components/SearchField/SearchField";
import { noteCalendarDay, noteMatchesDate } from "utils/noteDates";
import { adjustmentRequest } from "utils/adjustmentNoteAPI";
import DebitNoteResponsiveHeader from "../DebitNote/DebitNoteResponsiveHeader";
import "../DebitNote/debitNote.css";
import "./adjustmentNotes.css";
import "./noteDesign.css";
const initial = { search: "", status: "All", party: "All", date: "All" };
const filterText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
export const sourcePath = (note) =>
  note.sourceKind === "expense"
    ? "/expenses"
    : note.sourceKind === "rcti"
    ? `/rcti/view/${note.sourceID}`
    : `/invoices/view/${note.sourceInvoiceID || note.sourceID}`;
export default function NoteRegister({ credit = false }) {
  const [controller] = useMaterialUIController(),
    navigate = useNavigate();
  const kind = credit ? "credit" : "debit",
    base = credit ? "/creditNote" : "/debitNote";
  const [data, setData] = useState({ notes: [], canWrite: false }),
    [loading, setLoading] = useState(true),
    [error, setError] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [rangeDraft, setRangeDraft] = useState({ from: "", to: "" });
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rangeError, setRangeError] = useState("");
  const [filters, setFilters] = useState(initial),
    [page, setPage] = useState(1),
    [menu, setMenu] = useState(null);
  const login = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}");
    } catch {
      return {};
    }
  }, []);
  const organisation =
      login.businessName || login.companyName || "Your business",
    currency = login.currencyCode === "$" ? "AUD" : login.currencyCode || "AUD";
  const load = () => {
    setLoading(true);
    setError("");
    adjustmentRequest(kind, "", undefined, "get")
      .then(setData)
      .catch((e) => setError(e.response?.data?.Result_Message || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [credit]);
  const notes = data.notes.map((note) =>
    credit
      ? {
          ...note,
          number: note.creditNumber,
          contactName:
            note.customerIdentity?.name ||
            note.companyName ||
            note.customerName ||
            "Contact",
          reference: note.sourceSnapshot?.reference || note.purchaseOrderNumber,
          status: note.activityStatus || "Draft",
          total: note.finalAmount,
          currency: note.currencyValue || currency,
          createdAt: note.creditDate || note.createdDate,
        }
      : note,
  );
  const customers = [...new Set(notes.map((note) => note.contactName))].sort();
  const filtered = notes.filter((note) => {
    const searchText = [
      note.number,
      note.contactName,
      note.reference,
      note.reason,
      ...(!credit
        ? [
            note.direction === "customer" ? "To customer" : "To supplier",
            note.status,
            noteCalendarDay(note.createdAt),
          ]
        : []),
    ].join(" ");
    if (
      !(credit
        ? filterText(searchText).includes(filterText(filters.search))
        : searchText
            .toLowerCase()
            .includes(filters.search.trim().toLowerCase()))
    )
      return false;
    if (
      filters.status !== "All" &&
      (credit
        ? filterText(note.status) !== filterText(filters.status)
        : note.status !== filters.status)
    )
      return false;
    if (
      filters.party !== "All" &&
      (credit ? note.contactName : note.direction) !== filters.party
    )
      return false;
    if (filters.date === "Custom range") {
      const day = noteCalendarDay(note.createdAt);
      return Boolean(day && day >= dateRange.from && day <= dateRange.to);
    }
    return noteMatchesDate(note.createdAt, filters.date);
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / 10)),
    currentPage = Math.min(page, totalPages),
    start = (currentPage - 1) * 10,
    rows = filtered.slice(start, start + 10);
  const change = (key, value) => {
    if (key === "date" && value === "Custom range") {
      setRangeDraft(dateRange);
      setRangeError("");
      setRangeOpen(true);
      return;
    }
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };
  const select = (key, label, options) => (
    <ThemedSelect
      className={!credit && key === "date" ? "debit-register-date-filter" : ""}
      aria-label={label}
      dark={controller.darkMode}
      value={filters[key]}
      displayValue={
        filters[key] === "All"
          ? label
          : key === "date" && filters.date === "Custom range"
          ? `${dateRange.from} – ${dateRange.to}`
          : options.find((option) => option.value === filters[key])?.label ||
            filters[key]
      }
      onChange={(event) => change(key, event.target.value)}
    >
      <option value="All">All</option>
      {options.map((option) => (
        <option value={option.value || option} key={option.value || option}>
          {option.label || option}
        </option>
      ))}
    </ThemedSelect>
  );
  const clearFiltersButton = Object.keys(initial).some(
    (key) => initial[key] !== filters[key],
  ) && (
    <button
      type="button"
      className="debit-clear-filters"
      onClick={() => {
        setFilters(initial);
        setPage(1);
      }}
    >
      Clear filters
    </button>
  );
  const headers = credit
    ? [
        "Credit note number",
        "Customer",
        "Against invoice",
        "Date",
        "Reason",
        "Status",
        "Credit amount",
        "Actions",
      ]
    : [
        "Debit note number",
        "Direction",
        "Contact",
        "Linked document",
        "Reason",
        "Date",
        "Status",
        "Note amount",
        "Actions",
      ];
  const active = menu?.note;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <main
        className={`debit-page adjustment-register ${
          credit ? "note-register--credit" : "note-register--debit"
        } app-register-page-content${controller.darkMode ? " dark" : ""}`}
      >
        <DebitNoteResponsiveHeader
          organisation={organisation}
          role={login.roleName || "Owner"}
        />
        <header className="debit-page-heading">
          <div>
            <h1>{credit ? "Credit note" : "Debit note"}</h1>
            <p>
              {credit
                ? "Adjustments against invoices you’ve issued."
                : "Customer charges and supplier adjustment requests."}
            </p>
          </div>
          <div className="debit-page-actions">
            <button
              type="button"
              className="note-register-create-button"
              disabled={!loading && !data.canWrite}
              onClick={() => navigate(`${base}/add`)}
            >
              <LuPlus /> New {kind} note
            </button>
          </div>
          <span className="debit-page-meta">
            {organisation} · All amounts in {currency}
          </span>
        </header>
        {error && (
          <p role="alert" className="debit-backend-notice">
            {error} <button onClick={load}>Retry</button>
          </p>
        )}
        <section
          className="debit-filters"
          aria-label={`Search and filter ${kind} notes`}
        >
          <label className="debit-search">
            <LuSearch />
            <ClearableSearchInput
              aria-label={`Search ${kind} notes`}
              placeholder={
                credit
                  ? "Search number, customer or invoice"
                  : "Search note, contact or document"
              }
              value={filters.search}
              onChange={(event) => change("search", event.target.value)}
            />
          </label>
          <div className="debit-filter-group">
            {!credit &&
              select("party", "Direction", [
                { value: "customer", label: "To customer" },
                { value: "supplier", label: "To supplier" },
              ])}
            {select(
              "status",
              "Status",
              credit
                ? ["Draft", "Applied", "Refunded", "Issued"]
                : ["Draft", "Issued", "Awaiting confirmation", "Applied"],
            )}
            {credit && select("party", "Customer", customers)}
            {select("date", "Date range", [
              "Today",
              "Last 30 days",
              "This financial year",
              "Custom range",
            ])}
            {clearFiltersButton}
          </div>
        </section>
        <section className="note-register-card" aria-label={`${kind} notes`}>
          <div className="note-register-summary">
            <strong>
              {filtered.length} {kind}{" "}
              {filtered.length === 1 ? "note" : "notes"}
            </strong>
            <span>{currency}</span>
          </div>
          {loading && (
            <span className="skeleton-sr-only" role="status">
              Loading {kind} notes…
            </span>
          )}
          <div className="note-table-scroll">
            <table>
              <colgroup>
                {(credit ? [15, 17, 14, 10, 14, 10, 13, 7] : [12, 10, 15, 13, 12, 10, 10, 11, 7]).map((width, index) => (
                  <col key={headers[index]} style={{ width: `${width}%` }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton
                    variant="rows"
                    columns={headers.length - 1}
                    columnWidths={headers.slice(0, -1).map(() => "65%")}
                    hasActions
                    rows={5}
                  />
                ) : (
                  rows.map((note) => (
                    <tr key={note._id}>
                      <td>
                        <Link
                          className="note-reference"
                          to={`${base}/view/${note._id}`}
                        >
                          {note.number}
                        </Link>
                      </td>
                      {!credit && (
                        <td>
                          {note.direction === "customer"
                            ? "To customer"
                            : "To supplier"}
                        </td>
                      )}
                      <td className="note-contact" title={note.contactName}>
                        {note.contactName}
                      </td>
                      <td>
                        {note.sourceID || note.sourceInvoiceID ? (
                          <Link
                            className="note-reference"
                            to={sourcePath(note)}
                          >
                            {note.reference}
                          </Link>
                        ) : (
                          note.reference || "—"
                        )}
                      </td>
                      {credit && (
                        <td
                          title={new Date(note.createdAt).toLocaleString(
                            "en-AU",
                          )}
                        >
                          {new Date(note.createdAt).toLocaleDateString(
                            "en-AU",
                            { day: "numeric", month: "short" },
                          )}
                        </td>
                      )}
                      <td>{note.reason || "—"}</td>
                      {!credit && (
                        <td
                          title={new Date(note.createdAt).toLocaleString(
                            "en-AU",
                          )}
                        >
                          {new Date(note.createdAt).toLocaleDateString(
                            "en-AU",
                            { day: "numeric", month: "short", year: "numeric" },
                          )}
                        </td>
                      )}
                      <td>
                        <span
                          className={`note-status note-status-${note.status
                            .toLowerCase()
                            .replaceAll(" ", "-")}`}
                        >
                          {note.status}
                        </span>
                        {!credit && (
                          <small className="debit-card-state-copy">
                            {note.direction === "customer"
                              ? note.status === "Draft"
                                ? "To customer · Draft · No effect until issued"
                                : "To customer · Issue increases receivable · Issued, not paid"
                              : note.status === "Applied"
                              ? "To supplier · Confirmed reduction posted · Audit history preserved"
                              : note.status === "Draft"
                              ? "To supplier · Draft · No financial posting"
                              : "To supplier · Awaiting confirmation · No reduction posted"}
                          </small>
                        )}
                        {note.deliveryStatus === "failed" && (
                          <small className="note-delivery-failed">
                            Delivery failed
                          </small>
                        )}
                      </td>
                      <td className="note-amount">
                        {credit ? "−" : ""}
                        {new Intl.NumberFormat("en-AU", {
                          style: "currency",
                          currency:
                            note.currency === "$"
                              ? "AUD"
                              : note.currency || currency,
                        }).format(note.total || 0)}
                      </td>
                      <td>
                        <button
                          className="listing-actions-trigger"
                          aria-label={`Actions for ${note.number}`}
                          aria-haspopup="menu"
                          onClick={(event) =>
                            setMenu({ anchor: event.currentTarget, note })
                          }
                        >
                          <LuEllipsis />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && !rows.length && (
            <div className="note-empty">
              <h2>
                {data.notes.length
                  ? `No ${kind} notes match your filters`
                  : `No ${kind} notes yet`}
              </h2>
              <p>
                {data.notes.length
                  ? `Try a different ${kind} note number, contact, document or date range. Your notes are still here.`
                  : `Create a ${kind} note to record an adjustment against a linked document.`}
              </p>
              {!data.notes.length && data.canWrite && (
                <button
                  type="button"
                  className="note-register-create-button"
                  onClick={() => navigate(`${base}/add`)}
                >
                  New {kind} note
                </button>
              )}
            </div>
          )}
          <footer className="note-register-footer">
            <span>
              Showing {filtered.length ? start + 1 : 0}–
              {Math.min(start + 10, filtered.length)} of {filtered.length}{" "}
              {kind} notes
            </span>
            <div>
              <button
                aria-label="Previous page"
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              >
                <LuChevronLeft />
              </button>
              <button
                aria-label="Next page"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                <LuChevronRight />
              </button>
            </div>
          </footer>
        </section>
        <p className="note-register-guidance">
          {credit
            ? "Credits adjust issued invoices, not payments received. Drafts do not change invoice balances. Posted credits preserve audit history."
            : "Customer issue increases the invoice balance. Supplier notification does not post. Applied means a confirmed reduction was posted once. Drafts do not change balances; Issued is not Paid."}
        </p>
        <Dialog
          open={rangeOpen}
          onClose={() => setRangeOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            className: "note-date-range-dialog",
            "data-theme": controller.darkMode ? "dark" : "light",
          }}
          aria-labelledby="credit-date-range-title"
        >
          <h2 id="credit-date-range-title">
            {credit ? "Credit" : "Debit"} note date range
          </h2>
          {["from", "to"].map((field) => (
            <PickDate
              key={field}
              id={`${credit ? "credit" : "debit"}-range-${field}`}
              label={field === "from" ? "From" : "To"}
              selected={rangeDraft[field] ? parseISO(rangeDraft[field]) : null}
              minDate={field === "to" && rangeDraft.from ? parseISO(rangeDraft.from) : undefined}
              maxDate={field === "from" && rangeDraft.to ? parseISO(rangeDraft.to) : undefined}
              dateFormat="dd MMM yyyy"
              onChange={(date) => {
                const value = date ? format(date, "yyyy-MM-dd") : "";
                setRangeDraft((current) => ({ ...current, [field]: value }));
                setRangeError("");
              }}
            />
          ))}
          {rangeError && <p role="alert">{rangeError}</p>}
          <footer>
            <button
              type="button"
              className="debit-secondary-button"
              onClick={() => setRangeOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="note-register-create-button"
              onClick={() => {
                if (
                  !noteCalendarDay(rangeDraft.from) ||
                  !noteCalendarDay(rangeDraft.to) ||
                  rangeDraft.from > rangeDraft.to
                ) {
                  setRangeError("Choose valid start and end dates in order.");
                  return;
                }
                setDateRange(rangeDraft);
                setFilters((current) => ({ ...current, date: "Custom range" }));
                setPage(1);
                setRangeOpen(false);
              }}
            >
              Apply date range
            </button>
          </footer>
        </Dialog>
        <ListingActionMenu
          anchorEl={menu?.anchor}
          onClose={() => setMenu(null)}
          label="Note actions"
        >
          {active && [
            <Link
              key="view"
              to={`${base}/${
                credit && active.status === "Draft" && data.canWrite
                  ? "edit"
                  : "view"
              }/${active._id}`}
            >
              {credit
                ? active.status === "Draft" && data.canWrite
                  ? "View / Edit"
                  : "View credit note"
                : active.direction === "supplier" && active.status !== "Applied"
                ? "View request"
                : "View note"}
            </Link>,
            ...(active.status === "Draft" &&
            data.canWrite &&
            (!credit || active.sourceInvoiceID)
              ? [
                  ...(!credit
                    ? [
                        <Link key="edit" to={`${base}/edit/${active._id}`}>
                          Edit draft
                        </Link>,
                      ]
                    : []),
                  <Link key="issue" to={`${base}/edit/${active._id}`}>
                    {credit
                      ? "Apply credit"
                      : active.direction === "customer"
                      ? "Issue debit note"
                      : "Notify supplier"}
                  </Link>,
                ]
              : []),
            ...((active.sourceID || active.sourceInvoiceID) &&
            (credit || active.status !== "Draft")
              ? [
                  <Link key="source" to={sourcePath(active)}>
                    {active.sourceKind === "invoice" || active.sourceInvoiceID
                      ? credit
                        ? "View linked invoice"
                        : "View invoice"
                      : "View linked document"}
                  </Link>,
                ]
              : []),
            ...(!credit &&
            active.status === "Awaiting confirmation" &&
            data.canWrite
              ? [
                  <Link
                    key="confirm"
                    to={`${base}/view/${active._id}?action=confirm`}
                  >
                    Review confirmation
                  </Link>,
                  <Link
                    key="link"
                    to={`${base}/view/${active._id}?action=link`}
                  >
                    Link supplier adjustment
                  </Link>,
                ]
              : []),
            ...(!credit &&
            active.direction === "supplier" &&
            active.status === "Applied"
              ? [
                  <Link
                    key="support"
                    to={`${base}/view/${active._id}#supporting-adjustment`}
                  >
                    View supporting adjustment
                  </Link>,
                ]
              : []),
            ...(active.status !== "Draft"
              ? [
                  <Link key="audit" to={`${base}/view/${active._id}#activity`}>
                    {credit ? "View audit history" : "View audit"}
                  </Link>,
                ]
              : []),
            ...(!credit &&
            active.deliveryStatus === "failed" &&
            active.status !== "Draft" &&
            data.canWrite
              ? [
                  <Link
                    key="retry"
                    to={`${base}/view/${active._id}?action=delivery`}
                  >
                    Retry delivery
                  </Link>,
                ]
              : []),
          ]}
        </ListingActionMenu>
      </main>
    </DashboardLayout>
  );
}
